import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import Peer from "simple-peer";
import { StreamVideo } from "../StreamVideo";
import { DevicePermissions } from "../DevicePermissions";

const socket = io(process.env.REACT_APP_API_URL, {
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
});

const RTC_CONFIG = {
  iceServers: [
    {
      urls: [
        "stun:stun.l.google.com:19302",
        "stun:global.stun.twilio.com:3478",
      ],
    },
    {
      urls: [
        "turn:stun.evan-brass.net",
        "turn:stun.evan-brass.net?transport=tcp",
        "turn:stun.evan-brass.net?transport=udp",
        "stun:stun.evan-brass.net",
      ],
      username: "guest",
      credential: "password",
    },
    {
      urls: [
        "turn:turn.pickitforme.com",
        "turn:turn.pickitforme.com?transport=tcp",
        "turn:turn.pickitforme.com?transport=udp",
        "stun:stun.pickitforme.com",
      ],
      username: "user1",
      credential: "password1",
    },
  ],
};


export const useCallSocket = ({ user, videoOff, mute }) => {
  const connectionRef = useRef(null);
  const activeStreamRef = useRef(null);
  const remoteVideo = useRef(null);
  const Permissions = useRef(null);
  const isInCallWith = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);

  /* ---------------- Permissions ---------------- */

  useEffect(() => {
    Permissions.current = DevicePermissions(navigator.permissions);
  }, []);

  /* ---------------- Register Socket ---------------- */

  useEffect(() => {
    if (!user) return;

    socket.emit("setSocketId", {
      userId: user._id,
      userName: user.name,
      socketId: socket.id,
    });
  }, [user,socket?.id]);

  /* ---------------- Media Toggle ---------------- */

  useEffect(() => {
    (async () => {
      if (!Permissions.current) return;

      activeStreamRef.current = await StreamVideo({
        on: !videoOff && Permissions.current.camAllowed,
        video: !videoOff,
        audio: !mute,
      });

      if (connectionRef.current?.streams) {
        connectionRef.current.streams.forEach((stream) => {
          stream.getVideoTracks().forEach(t => (t.enabled = !videoOff));
          stream.getAudioTracks().forEach(t => (t.enabled = !mute));
        });
      }
    })();
  }, [videoOff, mute]);

  useEffect(() => {
    if (!user) return;

    socket.emit("setSocketId", {
      userId: user._id,
      userName: user.name,
      socketId: socket.id,
    });

    socket.on("calluser", ({ from, name, signal }) => {
      console.log("📞 Incoming call from", name);
      setIncomingCall({ from, name, signal });
    });

    socket.on("callended", cleanup);
    socket.on("userleft", cleanup);

    return () => {
      socket.off("calluser");
      socket.off("callended");
      socket.off("userleft");
    };
  }, [user]);

  const acceptCall = async () => {
    if (!incomingCall) return;

    activeStreamRef.current = await navigator.mediaDevices.getUserMedia({
      video: !videoOff,
      audio: !mute,
    });

    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream: activeStreamRef.current,
      config: {
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          {
            urls: process.env.REACT_APP_TURN_URL,
            username: process.env.REACT_APP_TURN_USERNAME,
            credential: process.env.REACT_APP_TURN_PASSWORD,
          },
        ],
      },
    });

    peer.on("signal", (data) => {
      socket.emit("answercall", {
        to: incomingCall.from,
        signal: data,
      });
    });

    peer.on("stream", (stream) => {
      remoteVideo.current.srcObject = stream;
    });

    peer.signal(incomingCall.signal);
    connectionRef.current = peer;
    setIncomingCall(null);
  };

  /* ---------- CLEANUP ---------- */

  const cleanup = () => {
    connectionRef.current?.destroy();
    connectionRef.current = null;
    setIncomingCall(null);
  };

  /* ---------------- Helpers ---------------- */

  const stopCall = () => {
    if (connectionRef.current) {
      connectionRef.current.destroy();
      connectionRef.current = null;
    }
    StreamVideo({ on: false });
  };

  /* ---------------- Receive Call ---------------- */

  const receiveCall = async ({ signal, from }) => {
    activeStreamRef.current = await StreamVideo({
      on: true,
      video: !videoOff,
      audio: !mute,
    });

    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream: activeStreamRef.current,
      config: RTC_CONFIG, // 🔥 TURN added
    });

    peer.on("signal", data => {
      socket.emit("answercall", { signal: data, to: from });
    });

    peer.on("close", stopCall);
    peer.on("error", console.error);

    socket.once("userleft", stopCall);
    socket.once("callended", stopCall);

    peer.signal(signal);
    connectionRef.current = peer;
  };

  /* ---------------- Call User ---------------- */

  const callUser = (socketId) => {
    isInCallWith.current = socketId;

    const peer = new Peer({
      initiator: true,
      trickle: false,
      config: RTC_CONFIG, // 🔥 TURN added
    });

    peer.on("signal", data => {
      socket.emit("calluser", {
        userToCall: socketId,
        signalData: data,
        from: socket.id,
        name: user.name,
      });
    });

    peer.on("stream", stream => {
      if (remoteVideo.current) {
        remoteVideo.current.srcObject = stream;
      }
    });

    socket.once("callaccepted", signal => {
      if (!peer.destroyed) peer.signal(signal);
    });

    socket.once("userleft", stopCall);
    socket.once("callended", stopCall);

    peer.on("close", stopCall);
    peer.on("error", console.error);

    connectionRef.current = peer;
  };

  /* ---------------- Socket listeners ---------------- */

  useEffect(() => {
    if (!user) return;

    socket.on("calluser", receiveCall);

    return () => {
      socket.off("calluser", receiveCall);
      stopCall();
    };
  }, [user]);

  return {
    remoteVideo,
    callUser,
    stopCall,
    incomingCall,
    acceptCall,
  };
};
